import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TrackByFunction
} from '@angular/core'
import { combineLatest, Subject } from 'rxjs'
import { distinctUntilChanged, map, merge, shareReplay } from 'rxjs/operators'
import { maybe } from 'typescript-monads'

type MultiCamWidgetIconChooser = (currentCount: number, link: MultiCamWidgetIconConfig, isPIP?: boolean) => string

interface MultiCamWidgetConfigResolved {
  links: ReadonlyArray<MultiCamWidgetIconConfig>
  initial: {
    count: number
    isPIP: boolean
  }
}

interface MultiCamWidgetIconConfig {
  count: number
  isPIP?: boolean
  title: string
  svg_on: string
  svg_off: string
  svgChooser: MultiCamWidgetIconChooser
  name: string
}

const DEFAULT_SVG_CHOOSER = (currentCount: number, link: any) => {
  return currentCount === link.count ? link.svg_on : link.svg_off
}

const DEFAULT_CONFIG: MultiCamWidgetConfigResolved = {
  initial: {
    count: 1,
    isPIP: false
  },
  links: [
    {
      count: 1,
      isPIP: false,
      title: 'single camera view',
      svg_on: 'vid_single_active',
      svg_off: 'vid_single_inactive',
      svgChooser: DEFAULT_SVG_CHOOSER,
      name: '1-up'
    },
    // {
    //   count: 2,
    //   isPIP: true,
    //   title: 'picture-in-picture view',
    //   svg_on: 'vid_pip_active',
    //   svg_off: 'vid_pip_inactive',
    //   svgChooser: (currentCount: number, link: any, isPIP?: boolean) => {
    //     return currentCount === link.count && isPIP ? link.svg_on : link.svg_off
    //   },
    //   name: 'PiP'
    // },
    {
      count: 2,
      isPIP: false,
      title: 'dual camera view',
      svg_on: 'vid_2up_active',
      svg_off: 'vid_2up_inactive',
      svgChooser: DEFAULT_SVG_CHOOSER,
      name: '2-up'
    },
    {
      count: 4,
      isPIP: false,
      title: 'quad camera view',
      svg_on: 'vid_4up_active',
      svg_off: 'vid_4up_inactive',
      svgChooser: DEFAULT_SVG_CHOOSER,
      name: '4-up'
    }
  ]
}

interface RenderedView {
  trackByLink: TrackByFunction<MultiCamWidgetIconConfig>
}

@Component({
  selector: 'flo-multi-cam-widget',
  templateUrl: './multi-cam-widget.component.html',
  styleUrls: ['./multi-cam-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiCamWidgetComponent implements OnChanges, AfterViewInit, RenderedView {
  @Input()
  get config(): MultiCamWidgetConfigResolved {
    return this._config
  }

  set config(val: MultiCamWidgetConfigResolved) {
    this._config = {
      ...DEFAULT_CONFIG,
      ...(val || {})
    }
  }

  @Input()
  get eventAnalytics(): any {
    return {
      ...this._eventAnalytics
    }
  }

  set eventAnalytics(val: any) {
    this._eventAnalytics = val
  }
  private multiCamChangeSource = new Subject<number>()
  private multiCamChange$ = this.multiCamChangeSource.pipe(distinctUntilChanged())
  private streamCountSource = new Subject<number>()
  private streamCount$ = this.streamCountSource.pipe(
    map(res => (res === 3 ? 4 : res)),
    shareReplay(1)
  )
  private _config = DEFAULT_CONFIG
  private _eventAnalytics: any
  @Input()
  streamCount: number
  @Input()
  multiCamChange: number

  @Output() readonly selectionChanged = new EventEmitter<number>()

  private selection$ = this.selectionChanged.pipe(shareReplay(1))

  view$ = combineLatest([this.streamCount$, this.selection$.pipe(merge(this.multiCamChange$))]).pipe(
    map(([streamCount, selection]) => {
      return {
        links: this.config.links
          .filter(link => streamCount >= link.count)
          .map(link => {
            return {
              ...link,
              analyticProps: {
                name: link.name,
                ...this.eventAnalytics
              },
              // TODO get this fixed up. need svg being selected some way...
              svg: link.svgChooser(selection, link)
            }
          })
      }
    })
  )

  ngAfterViewInit() {
    this.streamCountSource.next(this.streamCount)
    this.selectionChanged.next(this.config.initial.count)
  }

  _selectionChanged(cameraCount: number) {
    this.selectionChanged.next(cameraCount)
  }

  trackByLink: TrackByFunction<MultiCamWidgetIconConfig> = (_index: number, linkConfig: MultiCamWidgetIconConfig) => {
    return linkConfig.svg_on
  }

  ngOnChanges(changes: SimpleChanges) {
    // needing to update selected multiCam icon when changing from parent with hotkeys
    // and when streams switch off to update the icons based on streamCount
    maybe(changes.streamCount)
      .flatMap(change => maybe(change.currentValue))
      .tapSome(streamCount => this.streamCountSource.next(streamCount))

    maybe(changes.multiCamChange)
      .flatMap(change => maybe(change.currentValue))
      .tapSome(camCount => this.multiCamChangeSource.next(camCount))
  }
}
